import { HEALTH_CHECKS, HealthCheckInfo } from '../config/health-checks.config'
import { DASHBOARD_SECTIONS, DashboardSectionConfig } from '../config/dashboard-sections.config'

const SEVERITY_STYLES: Record<string, { bg: string; color: string; label: string }> = {
  critical: { bg: '#fef2f2', color: '#dc2626', label: 'Critical' },
  warning:  { bg: '#fffbeb', color: '#d97706', label: 'Warning' },
  info:     { bg: '#eff6ff', color: '#2563eb', label: 'Info' },
}

function SeverityBadge({ severity }: { severity: string }) {
  const s = SEVERITY_STYLES[severity] || SEVERITY_STYLES.info
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 4,
      fontSize: 11,
      fontWeight: 600,
      background: s.bg,
      color: s.color,
    }}>
      {s.label}
    </span>
  )
}

function StatusDot({ enabled }: { enabled: boolean }) {
  return (
    <span style={{
      display: 'inline-block',
      width: 8,
      height: 8,
      borderRadius: '50%',
      background: enabled ? '#059669' : '#9ca3af',
      marginRight: 8,
    }} />
  )
}

function CheckRow({ check }: { check: HealthCheckInfo }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr auto auto auto',
      gap: 12,
      alignItems: 'center',
      padding: '12px 16px',
      borderBottom: '1px solid #f3f4f6',
    }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <StatusDot enabled={check.enabled} />
          <span style={{ fontSize: 13, fontWeight: 600, color: '#111' }}>
            {check.label}
          </span>
        </div>
        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4, marginLeft: 16 }}>
          {check.description}
        </div>
      </div>
      <div style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>
        {check.metric}
      </div>
      <SeverityBadge severity={check.severity} />
      <div>
        {check.autoExclude ? (
          <span style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: 4,
            fontSize: 11,
            fontWeight: 600,
            background: '#fef2f2',
            color: '#dc2626',
          }}>
            Auto-exclude
          </span>
        ) : (
          <span style={{ fontSize: 11, color: '#9ca3af' }}>--</span>
        )}
      </div>
    </div>
  )
}

function SectionRow({ section }: { section: DashboardSectionConfig }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr auto',
      gap: 12,
      alignItems: 'center',
      padding: '12px 16px',
      borderBottom: '1px solid #f3f4f6',
    }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <StatusDot enabled={section.enabled} />
          <span style={{ fontSize: 13, fontWeight: 600, color: '#111' }}>
            {section.label}
          </span>
        </div>
        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4, marginLeft: 16 }}>
          {section.description}
        </div>
      </div>
      <div>
        <span style={{
          display: 'inline-block',
          padding: '2px 8px',
          borderRadius: 4,
          fontSize: 11,
          fontWeight: 600,
          background: section.enabled ? '#ecfdf5' : '#f3f4f6',
          color: section.enabled ? '#059669' : '#9ca3af',
        }}>
          {section.enabled ? 'Ativo' : 'Desativado'}
        </span>
      </div>
    </div>
  )
}

export function SettingsPage() {
  const enabledChecks = HEALTH_CHECKS.filter(c => c.enabled).length
  const autoExcludeChecks = HEALTH_CHECKS.filter(c => c.autoExclude).length
  const criticalChecks = HEALTH_CHECKS.filter(c => c.severity === 'critical').length

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      {/* HEALTH CHECKS */}
      <div style={{
        background: '#fff',
        borderRadius: 8,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: 24,
      }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#111' }}>
            Health Checks de Qualidade de Dados
          </h3>
          <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
            Regras aplicadas automaticamente durante o processamento de cada empresa.
            Alteracoes devem ser feitas no arquivo de configuracao do backend.
          </div>
          <div style={{ display: 'flex', gap: 16, marginTop: 10 }}>
            <span style={{ fontSize: 12, color: '#6b7280' }}>
              <strong>{enabledChecks}</strong> ativos de {HEALTH_CHECKS.length}
            </span>
            <span style={{ fontSize: 12, color: '#dc2626' }}>
              <strong>{criticalChecks}</strong> critical
            </span>
            <span style={{ fontSize: 12, color: '#d97706' }}>
              <strong>{autoExcludeChecks}</strong> com auto-exclude
            </span>
          </div>
        </div>

        {/* Table header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto auto auto',
          gap: 12,
          padding: '8px 16px',
          background: '#f9fafb',
          borderBottom: '1px solid #e5e7eb',
          fontSize: 11,
          fontWeight: 600,
          color: '#6b7280',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}>
          <div>Check</div>
          <div>Metrica</div>
          <div>Severidade</div>
          <div>Acao</div>
        </div>

        {HEALTH_CHECKS.map(check => (
          <CheckRow key={check.id} check={check} />
        ))}
      </div>

      {/* DASHBOARD SECTIONS */}
      <div style={{
        background: '#fff',
        borderRadius: 8,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#111' }}>
            Secoes do Dashboard
          </h3>
          <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
            Secoes visiveis na tela principal de resultados.
            Alteracoes devem ser feitas no arquivo de configuracao do frontend.
          </div>
        </div>

        {/* Table header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto auto',
          gap: 12,
          padding: '8px 16px',
          background: '#f9fafb',
          borderBottom: '1px solid #e5e7eb',
          fontSize: 11,
          fontWeight: 600,
          color: '#6b7280',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}>
          <div>Secao</div>
          <div>Visibilidade</div>
          <div>Status</div>
        </div>

        {DASHBOARD_SECTIONS.map(section => (
          <SectionRow key={section.id} section={section} />
        ))}
      </div>
    </div>
  )
}
